import { type JSX, useState } from "react";
import { type RowSelectionState } from "@tanstack/react-table";
import { ListIllustration } from "vega-illustrations";

import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { m } from "@vega-core/paraglide/messages";

import { useDealCurrency } from "../../hooks/useDealCurrency";
import { EditEstimatedMaxTicketSizeModal } from "../TrackingTable/components/EditEstimatedMaxTicketSizeModal";
import { EditEstimatedMinTicketSizeModal } from "../TrackingTable/components/EditEstimatedMinTicketSizeModal";

import { useProspectsListTableColumns } from "./hooks/useProspectsListTableColumns";
import { ProspectsListTableColumnId } from "./constants";

type ModalState = {
  activeModal: "estimatedMax" | "estimatedMin" | null;
  selectedItem: DealShortlistItemResponse | null;
};

type ProspectsListTableProps = {
  dealId: string;
  canEdit: boolean;
  canApprove?: boolean;
  onApproveProspect?: (prospectId: string) => void;
  approvingProspectId?: string | null;
  prospectsData: DealShortlistItemResponse[];
  isLoading: boolean;
  onUploadClick?: () => void;
  onRemoveProspect?: (prospectId: string) => void;
  removingProspectId?: string | null;
  onFindFromSalesforce?: () => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (
    updater:
      | RowSelectionState
      | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
  onOpenNotesModal?: (rowIndex: number) => void;
  paginationElement?: JSX.Element | null;
};

export const ProspectsListTable = ({
  dealId,
  canEdit,
  canApprove,
  onApproveProspect,
  approvingProspectId,
  prospectsData,
  isLoading,
  onUploadClick,
  onRemoveProspect,
  removingProspectId,
  onFindFromSalesforce,
  rowSelection = {},
  onRowSelectionChange,
  onOpenNotesModal,
  paginationElement,
}: ProspectsListTableProps) => {
  const { currency } = useDealCurrency(dealId);

  const [modalState, setModalState] = useState<ModalState>({
    activeModal: null,
    selectedItem: null,
  });

  const onOpenEstMinModal = (item: DealShortlistItemResponse) => {
    setModalState({ activeModal: "estimatedMin", selectedItem: item });
  };

  const onOpenEstMaxModal = (item: DealShortlistItemResponse) => {
    setModalState({ activeModal: "estimatedMax", selectedItem: item });
  };

  const closeModal = () => {
    setModalState({ activeModal: null, selectedItem: null });
  };

  const columns = useProspectsListTableColumns({
    dealId,
    canEdit,
    canApprove,
    onApproveProspect,
    approvingProspectId,
    onRemoveProspect,
    removingProspectId,
    onOpenNotesModal,
    onOpenEstMinModal,
    onOpenEstMaxModal,
  });

  return (
    <>
      <CoreTable
        columns={columns}
        data={prospectsData}
        dataTestId="prospects-table"
        dimUnselectableRowsOnMobile={false}
        disableResetSelectionOnDataChange
        emptyState={{
          title: m.coInvestments_addProspectsToYourList(),
          description: (
            <div className="flex flex-col gap-2 items-center justify-center">
              <p className="body-base-regular text-text/secondary">
                {m.coInvestments_addProspectsDescription()}
              </p>
              <div className="flex gap-2 mt-6">
                {onUploadClick ? (
                  <ThemedButton
                    dataTestId="upload-csv-button-empty-state"
                    onClick={onUploadClick}
                    size="small"
                    variant="secondary"
                  >
                    {m.common_uploadCsv()}
                  </ThemedButton>
                ) : null}
                {onFindFromSalesforce ? (
                  <ThemedButton
                    dataTestId="find-from-salesforce-button-empty-state"
                    onClick={onFindFromSalesforce}
                    size="small"
                    variant="primary"
                  >
                    {m.coInvestments_prospects_emptyState_importFromSalesforce()}
                  </ThemedButton>
                ) : null}
              </div>
            </div>
          ),
          illustration: <ListIllustration />,
        }}
        enableRowSelection
        getCanSelect={(row) => !row.isApproved}
        getRowId={(row) => row.id}
        hideUnselectableCheckboxes
        initialSorting={[{ id: ProspectsListTableColumnId.Tier, desc: false }]}
        isLoading={isLoading}
        noSorting={false}
        onRowSelectionChange={onRowSelectionChange}
        paginationElement={paginationElement}
        selectedRows={rowSelection}
      />

      {modalState.activeModal === "estimatedMin" && modalState.selectedItem ? (
        <EditEstimatedMinTicketSizeModal
          dealCurrency={currency}
          dealId={dealId}
          dealShortlistItemId={modalState.selectedItem.id}
          initialValues={{
            estimatedTicketSizeMin:
              modalState.selectedItem.estimatedTicketSizeMin,
            estimatedTicketSizeMax:
              modalState.selectedItem.estimatedTicketSizeMax,
          }}
          isOpen
          onClose={closeModal}
        />
      ) : null}

      {modalState.activeModal === "estimatedMax" && modalState.selectedItem ? (
        <EditEstimatedMaxTicketSizeModal
          dealCurrency={currency}
          dealId={dealId}
          dealShortlistItemId={modalState.selectedItem.id}
          initialValues={{
            estimatedTicketSizeMin:
              modalState.selectedItem.estimatedTicketSizeMin,
            estimatedTicketSizeMax:
              modalState.selectedItem.estimatedTicketSizeMax,
          }}
          isOpen
          onClose={closeModal}
        />
      ) : null}
    </>
  );
};
